"use client";

/* eslint-disable @next/next/no-img-element */
import * as React from "react"

import { cn } from "@/lib/utils"

export interface VideoProps
  extends React.VideoHTMLAttributes<HTMLVideoElement> {
  mp4Src: string;
  webmSrc: string;
  fallbackSrc?: string;
  lazy?: boolean;
  playWhenVisible?: boolean;
  rootMargin?: string;
}

const Video = React.forwardRef<HTMLVideoElement, VideoProps>(
  ({
    className,
    mp4Src,
    webmSrc,
    fallbackSrc,
    lazy = false,
    playWhenVisible = false,
    rootMargin = "250px",
    autoPlay = true,
    preload,
    poster,
    onPlay,
    onEnded,
    onLoadedData,
    onCanPlay,
    ...props
  }, ref) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const [shouldLoad, setShouldLoad] = React.useState(!lazy && !playWhenVisible);
    const [isVisible, setIsVisible] = React.useState(!playWhenVisible);
    const [hasFrame, setHasFrame] = React.useState(!lazy && !playWhenVisible);
    const [hasEnded, setHasEnded] = React.useState(false);

    React.useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

    React.useEffect(() => {
      if (!shouldLoad) {
        setHasFrame(false);
      }
      setHasEnded(false);
    }, [mp4Src, shouldLoad, webmSrc]);

    React.useEffect(() => {
      if (!lazy && !playWhenVisible) {
        return;
      }
      const video = videoRef.current;
      if (!video) {
        return;
      }
      if (!("IntersectionObserver" in window)) {
        setShouldLoad(true);
        setIsVisible(true);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          const visible = entry.isIntersecting;
          if (visible) {
            setShouldLoad(true);
          }
          setIsVisible(visible);
        },
        { rootMargin },
      );

      observer.observe(video);
      return () => observer.disconnect();
    }, [lazy, playWhenVisible, rootMargin]);

    React.useEffect(() => {
      if (!playWhenVisible || !shouldLoad) {
        return;
      }
      const video = videoRef.current;
      if (!video) {
        return;
      }
      if (!isVisible) {
        video.pause();
        return;
      }
      if (autoPlay) {
        void video.play().catch(() => {
          // Browsers may block or pause background video to save power.
        });
      }
    }, [autoPlay, isVisible, playWhenVisible, shouldLoad]);

    const showPlaceholder = Boolean(fallbackSrc && (lazy || playWhenVisible) && (!hasFrame || hasEnded));

    return (
      <div
        className={cn(
          "relative w-full h-full overflow-hidden bg-background",
          className,
        )}
      >
        {showPlaceholder && fallbackSrc ? (
          <img
            src={fallbackSrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
      <video
        ref={videoRef}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          showPlaceholder ? "opacity-0" : "opacity-100",
          className,
        )}
        controls={false}
        preload={preload ?? (lazy || playWhenVisible ? "metadata" : "auto")}
        autoPlay={playWhenVisible ? false : autoPlay}
        muted={true}
        loop={true}
        poster={poster ?? fallbackSrc}
        onPlay={(event) => {
          setHasEnded(false);
          onPlay?.(event);
        }}
        onLoadedData={(event) => {
          setHasFrame(true);
          onLoadedData?.(event);
        }}
        onCanPlay={(event) => {
          setHasFrame(true);
          onCanPlay?.(event);
        }}
        onEnded={(event) => {
          setHasEnded(true);
          onEnded?.(event);
        }}
        {...props}
      >
        {shouldLoad ? (
          <>
            <source src={mp4Src} type="video/mp4" />
            <source src={webmSrc} type="video/webm" />
          </>
        ) : null}
        {
          !shouldLoad && fallbackSrc && !lazy && !playWhenVisible ? (
            <img
              src={fallbackSrc}
              alt="Fallback"
              className={cn(
                "w-full h-full object-cover",
                className,
              )} />
          ) : null
        }
      </video>
      </div>
    )
  }
)
Video.displayName = "Video"

export { Video }
